import React from "react";
import { motion } from "framer-motion";
import {
  SquaresFour,
  ChartBar,
  Wallet,
  Package,
  IdentificationBadge,
  UsersThree,
  ShieldCheck,
  SlidersHorizontal,
  Headset,
  Lock,
  Bank,
  ListChecks,
  FileText,
} from "@phosphor-icons/react";
import PageHero from "../components/site/PageHero";
import { APP_FEATURES } from "../data/site";

const iconMap = {
  SquaresFour,
  ChartBar,
  Wallet,
  Package,
  IdentificationBadge,
  UsersThree,
  ShieldCheck,
  SlidersHorizontal,
  Headset,
};

const detailedBlocks = [
  {
    title: "Reports built like a control room",
    description:
      "Ledger Book stitches every transaction in one place. Business Report rolls up your retail performance. Add Money Report tracks each top-up and Service-Wise Report breaks down volume by service.",
    items: ["Ledger Book", "Business Report", "Add Money Report", "Service-Wise Report"],
    Icon: ChartBar,
  },
  {
    title: "Flexible wallet & top-ups",
    description:
      "Top-up your retailer wallet in seconds via Manual Request, UPI/Bank Transfer or Instant UPI Payment — and start serving customers right away.",
    items: ["Smart Wallet", "Manual Request", "UPI / Bank Transfer", "Instant UPI Payment"],
    Icon: Wallet,
  },
  {
    title: "Security retailers can trust",
    description:
      "Multi-layer protection — password, T-PIN management (Set / Reset / Change) and App Lock with a 4-digit PIN or device biometric.",
    items: ["Change Password", "T-PIN Management", "App Lock (PIN / Device / None)"],
    Icon: ShieldCheck,
  },
  {
    title: "Settings that respect your shop floor",
    description:
      "Hide upcoming services, mask balance & commission on the sidebar and transaction screens, manage Bank Master, view activated services and per-service commission.",
    items: [
      "Hide Coming Soon Services",
      "Balance Hide & Eye Toggle",
      "Commission Hide on Status",
      "Bank Master (Add / Manage)",
      "Service Activation View",
      "Service Commission View",
    ],
    Icon: SlidersHorizontal,
  },
];

export default function Features() {
  return (
    <div data-testid="features-page">
      <PageHero
        eyebrow="Retailer App Features"
        title="A serious tool for"
        highlight="serious retailers"
        description="Dashboard, wallets, reports, refer & earn, security and settings — every screen designed for high-volume retail counters."
        testId="features-hero"
      />

      {/* Feature grid */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {APP_FEATURES.map((f, i) => {
              const Icon = iconMap[f.icon] || ListChecks;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: i * 0.04 }}
                  data-testid={`feature-card-${i}`}
                  className="group bg-surfaceAlt border border-line rounded-3xl p-7 hover:-translate-y-1 hover:shadow-xl hover:border-brandBlue/40 transition-all"
                >
                  <div className="h-14 w-14 rounded-2xl brand-icon-tile flex items-center justify-center mb-5 group-hover:scale-105 transition-transform">
                    <Icon size={26} weight="duotone" className="text-ink900" />
                  </div>
                  <h3 className="font-heading font-extrabold text-ink900 text-xl tracking-tight mb-2">
                    {f.title}
                  </h3>
                  <p className="text-sm text-ink700 leading-relaxed">
                    {f.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Deep-dive blocks */}
      <section className="py-24 bg-surfaceAlt border-y border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 space-y-8">
          {detailedBlocks.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.05 }}
              className={`grid lg:grid-cols-12 gap-8 items-center bg-surface border border-line rounded-3xl p-7 sm:p-12 ${
                i % 2 === 1 ? "lg:[direction:rtl]" : ""
              }`}
              data-testid={`features-deep-${i}`}
            >
              <div className="lg:col-span-5 lg:[direction:ltr]">
                <div className="h-16 w-16 rounded-2xl brand-icon-tile-dark flex items-center justify-center mb-5">
                  <b.Icon size={28} weight="duotone" />
                </div>
                <h3 className="font-heading font-extrabold text-ink900 text-2xl sm:text-3xl tracking-tight mb-3">
                  {b.title}
                </h3>
                <p className="text-ink700 leading-relaxed">{b.description}</p>
              </div>
              <div className="lg:col-span-7 lg:[direction:ltr] grid sm:grid-cols-2 gap-3">
                {b.items.map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-2.5 p-4 rounded-2xl bg-surfaceAlt border border-line"
                  >
                    <ListChecks size={18} weight="duotone" className="text-infoText" />
                    <span className="text-sm font-semibold text-ink900">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* KYC flow */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="max-w-3xl mb-10">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-infoText mb-3">
              KYC, the modern way
            </div>
            <h2 className="font-heading font-extrabold text-ink900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Five clean steps from <span className="text-gradient-brand">signup to verified</span>.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { Icon: IdentificationBadge, title: "Basic Info", text: "Name, business and contact." },
              { Icon: Lock, title: "Aadhaar eKYC", text: "Verify identity instantly." },
              { Icon: FileText, title: "PAN Verify", text: "Tax ID validation." },
              { Icon: Bank, title: "Bank Verify", text: "Penny-drop check." },
              { Icon: ListChecks, title: "Upload Docs", text: "Aadhaar, PAN, cheque, photo." },
            ].map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-surfaceAlt border border-line rounded-2xl p-5 hover:border-brandBlue/40 transition-colors"
                data-testid={`kyc-step-${i}`}
              >
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink500 mb-2">
                  Step {i + 1}
                </div>
                <s.Icon size={26} weight="duotone" className="text-infoText mb-3" />
                <div className="text-sm font-bold text-ink900 mb-1">{s.title}</div>
                <div className="text-xs text-ink700">{s.text}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
